const MAX_SAFE_INTEGER = BigInt("9007199254740991");

function isJsonWhitespace(ch: string): boolean {
  return ch === " " || ch === "\n" || ch === "\r" || ch === "\t";
}

function isNumberPrefix(ch: string): boolean {
  return ch === ":" || ch === "[" || ch === ",";
}

function isNumberSuffix(ch: string | undefined): boolean {
  return ch === "," || ch === "}" || ch === "]";
}

function skipJsonString(json: string, start: number): number {
  let index = start + 1;
  while (index < json.length) {
    if (json[index] === "\\") {
      index += 2;
      continue;
    }
    if (json[index] === '"') {
      return index + 1;
    }
    index += 1;
  }
  return json.length;
}

function shouldQuoteInteger(raw: string): boolean {
  const digits = raw.startsWith("-") ? raw.slice(1) : raw;
  if (digits.length < 16) {
    return false;
  }
  if (digits.length > 16) {
    return true;
  }
  const value = BigInt(raw);
  return value < -MAX_SAFE_INTEGER || value > MAX_SAFE_INTEGER;
}

/**
 * Snowflake ids are larger than Number.MAX_SAFE_INTEGER. JSON.parse turns
 * them into imprecise numbers, so a Package Reference saved from that value
 * points at the wrong row. Quote those integers before parsing.
 */
export function quoteUnsafeJsonIntegers(json: string): string {
  let result = "";
  let index = 0;

  while (index < json.length) {
    const current = json[index];
    if (current === '"') {
      const end = skipJsonString(json, index);
      result += json.slice(index, end);
      index = end;
      continue;
    }
    if (!isNumberPrefix(current)) {
      result += current;
      index += 1;
      continue;
    }

    result += current;
    index += 1;
    const whitespaceStart = index;
    while (index < json.length && isJsonWhitespace(json[index])) {
      index += 1;
    }

    const numberStart = index;
    if (json[index] === "-") {
      index += 1;
    }
    const digitStart = index;
    while (index < json.length && json[index] >= "0" && json[index] <= "9") {
      index += 1;
    }
    if (index === digitStart) {
      result += json.slice(whitespaceStart, index);
      continue;
    }

    let suffixIndex = index;
    while (suffixIndex < json.length && isJsonWhitespace(json[suffixIndex])) {
      suffixIndex += 1;
    }
    if (!isNumberSuffix(json[suffixIndex])) {
      result += json.slice(whitespaceStart, index);
      continue;
    }

    const raw = json.slice(numberStart, index);
    result += json.slice(whitespaceStart, numberStart);
    result += shouldQuoteInteger(raw) ? `"${raw}"` : raw;
  }

  return result;
}

export function parseJsonPreservingIds(text: string): unknown {
  return JSON.parse(quoteUnsafeJsonIntegers(text));
}

/** Axios transformResponse: keep unsafe integer ids as strings. */
export function transformResponsePreservingIds(data: unknown): unknown {
  if (typeof data !== "string") {
    return data;
  }
  const trimmed = data.trim();
  if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) {
    return data;
  }
  try {
    return parseJsonPreservingIds(trimmed);
  } catch {
    return data;
  }
}
